<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="page-service-category page"
    :class="{ 'empty': !showContentPage && !isLoading }"
  >
    <BreadCrumbs
      v-if="showContentPage"
      class="breadcrumbs"
      :bread-crumbs="breadCrumbsData"
    />
    <section class="service-list-section">
      <SectionTitle
        v-if="showContentPage"
        id="sectionTitle"
        class="service-list-section__title"
        :title="service.parentTypeName"
      />
      <v-tabs
        v-if="categoryList.length && setIsMobileDone"
        ref="tabsBar"
        :key="`tabs-${serviceParentTypeId}-${categoryList.length}`"
        :value="activeTab"
        class="fixed-tabs-bar overflow-x-auto ec-tabs"
        :centered="isMobile"
        :align-with-title="!isMobile"
        hide-slider
      >
        <v-tab
          v-for="tab in categoryList"
          :key="tab.typeId"
          class="ec-tab"
          @click="onTabClick(tab)"
        >
          <div class="ec-tab-inner">
            {{ tab.typeName }}
          </div>
        </v-tab>
      </v-tabs>
      <template v-else-if="showContentPage && serviceParentTypeId !== 'star'">
        <div class="service-list-section__tab__loader">
          <v-skeleton-loader
            v-for="x in 4"
            :key="x"
            type="chip"
            height="48"
            class="loader"
          />
        </div>
      </template>
      <transition name="fade">
        <GroupServiceList
          v-if="showSubCategoryContentPage && !isLoading"
          class="service-list-section__list"
          :service-list="service.list"
          :service-parent-type-id="serviceParentTypeId"
        />
      </transition>
      <ComponentPagination
        v-if="paginationLength > 1"
        class="service-list-pagination"
        :total-page="paginationLength"
        :current-page="activePage"
        @changeActivePageHandler="changeActivePageHandler"
      />
    </section>
    <client-only>
      <ServiceNotAvaliable v-if="!showContentPage && !isLoading" />
    </client-only>
  </div>
</template>

<script>
import ServiceNotAvaliable from '../components/Error/ServiceNotAvaliable.vue'
import SectionTitle from '@/components/ComponentSectionTitle'
import BreadCrumbs from '@/components/ComponentBreadCrumbs.vue'
import GroupServiceList from '@/components/GroupServiceList'
import ComponentPagination from '@/components/ComponentPagination'
import { mapGetters, mapState } from 'vuex'

/**
 * 格式化服務數據，將原始服務物件轉換為標準格式
 * @param {object} service - 原始服務物件
 * @returns {object} 格式化後的服務物件
 */
const handleFormatService = service => {
  return {
    serviceId: service.ServiceId,
    typeId: service.TypeId || null,
    typeName: service.TypeName || null,
    iconName: service.IconName || null,
    title: service.Title,
    imgUrl: service.imgUri,
    desc: service.Description,
    hasPromotion: service.HasPromotion,
    serviceAddressMode: service.ServiceAddressMode, // 1:地址模式 2:社區模式
    minimumPriceOrPricingMethod: {
      content: service.MinimumPriceOrPricingMethod.Content,
      isNumber: service.MinimumPriceOrPricingMethod.IsNumber
    },
    ratingSummary: {
      totalRatings: service.RatingSummary.TotalRatings,
      averageRating: service.RatingSummary.AverageRating
    }
  }
}
/**
 * 根據服務列表建立分類列表，將相同 typeId 的服務歸類
 * @param {Array} mainArray - 原始服務陣列
 * @returns {Array} 分類列表，每個分類包含 typeId、typeName 和 serviceList，按照首次出現順序排序
 */
const createCategoryList = (mainArray) => {
  const categoryMap = new Map() // 使用 Map 來處理唯一性和順序
  mainArray.forEach(svc => {
    const tId = svc.typeId || svc.TypeId
    const tName = svc.typeName || svc.TypeName
    if (tId == null) return

    if (!categoryMap.has(tId)) {
      categoryMap.set(tId, {
        typeId: tId,
        typeName: tName || '',
        serviceList: []
      })
    }
    categoryMap.get(tId).serviceList.push(svc)
  })
  return Array.from(categoryMap.values())
}
export default {
  components: {
    ServiceNotAvaliable,
    SectionTitle,
    BreadCrumbs,
    GroupServiceList,
    ComponentPagination
  },
  async asyncData ({ route, env, store }) {
    // 在 server 端計算初始 activeTab
    let initialActiveTab = 0
    const initialTabQuery = route.query && route.query.tab ? route.query.tab : null
    if (initialTabQuery && route.params.id !== 'star') {
      try {
        await store.dispatch('service/getAllService', { refresh: false, isApp: store.state.isApp })
        const parentId = parseInt(route.params.id)
        const matched = Array.isArray(store.state.service.allServiceList)
          ? store.state.service.allServiceList.find(item => item.typeParent === parentId)
          : null
        if (matched && Array.isArray(matched.Main)) {
          const categoryList = createCategoryList(matched.Main)
          const idx = categoryList.findIndex(tab => String(tab.typeId) === String(initialTabQuery))
          if (idx !== -1) {
            initialActiveTab = idx
          }
        }
      } catch (err) {
        console.log('asyncData tab calculation error:', err)
      }
    }
    return {
      routeFullPath: route.fullPath,
      webBaseUrl: String(env.BASE_WEB_URL || ' ').slice(0, -1),
      activeTab: initialActiveTab
    }
  },
  data () {
    return {
      breadCrumbsData: [],
      service: {
        parentTypeName: '',
        parentTypeDescription: '',
        list: [],
        allList: []
      },
      serviceData: null, // 原始服務數據
      activePage: 1, // 當前頁碼
      paginationLength: 1, // 分頁總數
      perPageDataCounts: 12, // 每頁數據數量
      setIsMobileDone: false, // 移動端判定完成旗標
      activeTab: 0, // 當前活躍 Tab 索引
      categoryList: [], // 分類列表
      observer: null, // IntersectionObserver 實例
      isScrolling: false // 滾動狀態旗標
    }
  },
  /**
   * 獲取服務數據，處理分類和分頁邏輯
   */
  async fetch () {
    try {
      if (this.serviceParentTypeId === 'star') {
        this.service.parentTypeName = '推薦服務'
        this.serviceData = await this.$store.dispatch('service/getStarServiceList')
        this.breadCrumbsData = [{ label: '推薦服務', routeName: 'index-id', params: { id: 'star' } }]
      } else {
        const serviceByParentType = await this.$store.dispatch('service/getServicesByParentType', { TypeParent: this.serviceParentTypeId })
        this.serviceData = serviceByParentType.ServiceList.map(handleFormatService)
        this.service.parentTypeName = serviceByParentType.ParentTypeName
        this.service.parentTypeDescription = serviceByParentType.ParentTypeDescription
        this.breadCrumbsData = [{ label: this.service.parentTypeName || '(名稱為空)', routeName: 'index-id', params: { id: this.serviceParentTypeId } }]
      }
      this.service.allList = Array.isArray(this.serviceData) ? this.serviceData : []
      await this.$store.dispatch('service/getAllService', { refresh: false, isApp: this.$store.state.isApp })
      this.setCategoryHandler()
      this.service.allList = this.service.allList.map(item => {
        // 如果在 categoryList 找到對應的 serviceList 中找到符合的 serviceId，則帶入 typeId
        const category = this.categoryList.find(cat => cat.serviceList.some(service => service.serviceId === item.serviceId))
        return {
          ...item,
          typeId: category ? category.typeId : null
        }
      })
      this.service.list = this.activeServiceData.slice((this.activePage - 1) * this.perPageDataCounts, this.perPageDataCounts)
      this.paginationLength = Math.ceil(this.activeServiceData.length / this.perPageDataCounts)

      if (this.categoryList.length > 0 && this.setIsMobileDone) {
        this.$nextTick(() => {
          this.scrollToTabHandler()
        })
      }
    } catch (err) {
      console.log('err', err)
    }
  },
  head () {
    return {
      title: this.service.parentTypeName,
      meta: [
        { hid: 'title', property: 'title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        { hid: 'og:url', property: 'og:url', content: this.webBaseUrl + this.routeFullPath },
        { hid: 'og:title', property: 'og:title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        this.service.parentTypeDescription ? { hid: 'og:description', property: 'og:description', content: this.service.parentTypeDescription } : {},
        { hid: 'twitter:url', property: 'twitter:url', content: this.webBaseUrl + this.routeFullPath },
        { hid: 'twitter:title', property: 'twitter:title', content: this.service.parentTypeName + this.$store.state.meta.titleTemple },
        this.service.parentTypeDescription ? { hid: 'twitter:description', property: 'twitter:description', content: this.service.parentTypeDescription } : {}
      ]
    }
  },
  computed: {
    ...mapGetters(['isMobile']), // 獲取是否為移動端
    ...mapState({ allServiceList: state => state.service.allServiceList }), // 獲取所有服務列表
    /**
     * 獲取服務父類型 ID
     * @returns {string} 服務父類型 ID
     */
    serviceParentTypeId () {
      return this.$nuxt.$route.params.id
    },
    /**
     * 判斷是否顯示內容頁面
     * @returns {boolean} 是否顯示
     */
    showContentPage () {
      return Array.isArray(this.service.allList) && this.service.allList.length > 0
    },
    /**
     * 判斷是否顯示子分類內容頁面
     * @returns {boolean} 是否顯示
     */
    showSubCategoryContentPage () {
      return Array.isArray(this.service.list) && this.service.list.length > 0
    },
    isLoading () {
      return this.$fetchState.pending
    },
    activeTypeId () {
      return this.categoryList[this.activeTab]?.typeId
    },
    activeServiceData () {
      if (!Array.isArray(this.serviceData)) return []
      if (!this.activeTypeId) return this.serviceData
      return this.service.allList.filter(svc => svc.typeId === this.activeTypeId)
    },
    canStartListenToDom () {
      return this.showContentPage && process.client
    }
  },
  watch: {
    async serviceParentTypeId (val, oldVal) {
      if (val === oldVal) return
      try {
        if (val) {
          this.activePage = 1
          this.$vuetify.goTo('#header', { duration: 300, offset: 0, easing: 'easeInOutQuad' })
          await this.$fetch()
        }
      } catch (err) {
        console.log('err', err)
      }
    },
    '$route.query.tab' (val, oldVal) {
      if (val === oldVal) return
      this.setCategoryHandler()
      this.setTabByQuery(val)
      this.$nextTick(() => {
        this.scrollToTabHandler()
      })
    },
    activeTab (val) {
      const category = this.categoryList[val]
      this.$gaLogEvent && this.$gaLogEvent('分類', category?.typeName)
    },
    activeServiceData (val) {
      this.activePage = 1
      this.service.list = val.slice((this.activePage - 1) * this.perPageDataCounts, this.perPageDataCounts * this.activePage)
      this.paginationLength = Math.ceil(val.length / this.perPageDataCounts)
    },
    canStartListenToDom (val) {
      if (val) {
        this.$nextTick(() => {
          this.listenTabSticky()
        })
      }
    }
  },
  mounted () {
    this.$store.commit('setWindowWidth', window.innerWidth)
    this.setIsMobileDone = true // 設定 mobile 判定完成旗標
  },
  beforeDestroy () {
    this.removeListenTabSticky()
  },
  methods: {
    /**
     * 處理分頁切換，更新當前頁面數據並滾動至 Tab
     * @param {number} page - 目標頁碼
     */
    async changeActivePageHandler (page) {
      this.$nextTick(() => {
        this.scrollToTabHandler(true)
      })
      this.activePage = page
      this.service.list = this.activeServiceData.slice((page - 1) * this.perPageDataCounts, this.perPageDataCounts * page)
    },
    /**
     * 設定分類列表，根據服務父類型建立分類
     */
    setCategoryHandler () {
      if (this.serviceParentTypeId === 'star') {
        this.categoryList = []
        return
      }

      const parentId = parseInt(this.serviceParentTypeId)
      const matched = Array.isArray(this.allServiceList) ? this.allServiceList.find(item => item.typeParent === parentId) : null
      if (!matched || !Array.isArray(matched.Main)) {
        this.categoryList = []
        return
      }

      this.categoryList = createCategoryList(matched.Main)
    },
    /**
     * 移除 Tab 黏性監聽，清理 IntersectionObserver
     */
    removeListenTabSticky () {
      if (!this.showContentPage) return
      const observeElSelector = '#sectionTitle'
      const observeEl = document.querySelector(observeElSelector)
      if (observeEl && this.observer) {
        this.observer.unobserve(observeEl)
        this.observer.disconnect()
        this.observer = null
      }
    },
    /**
     * 監聽 Tab 黏性行為，使用 IntersectionObserver 控制固定位置
     */
    listenTabSticky () {
      if (!this.showContentPage) return
      const observeElSelector = '#sectionTitle'
      const observeEl = document.querySelector(observeElSelector)
      if (!observeEl) return
      this.observer = new IntersectionObserver((entries) => {
        const tabBar = document.querySelector('.fixed-tabs-bar .v-tabs-bar')
        if (!tabBar) return
        tabBar.classList.toggle('is-fixed', !entries[0].isIntersecting)
      })
      this.observer.observe(observeEl)
    },
    /**
     * 滾動至 Tab 位置，處理移動端和桌面端偏移
     * @param {boolean} isPageChange - 是否為頁面變更
     */
    scrollToTabHandler (isPageChange) {
      if (this.isScrolling) {
        return
      }

      if (!process.client || !this.$refs.tabsBar) return

      if (!this.$route?.query?.tab && !isPageChange) {
        return
      }

      const tabsEl = this.$refs.tabsBar.$el
      if (!tabsEl) return

      const tabsClientHeight = tabsEl.clientHeight || 48
      const offsetHeight = this.isMobile ? 0 : 20

      // 設定滾動狀態
      this.isScrolling = true

      this.$vuetify.goTo('.fixed-tabs-bar', {
        duration: 500,
        offset: tabsClientHeight + offsetHeight,
        easing: 'easeInOutQuad'
      }).then(() => {
        this.isScrolling = false
      }).catch((error) => {
        this.isScrolling = false
        if (process.env.NODE_ENV === 'development') {
          console.error('Scroll error:', error)
        }
      })
    },
    /**
     * 根據 typeId 設定 activeTab
     * @param {string|number} typeId
     */
    setTabByQuery (typeId) {
      if (!Array.isArray(this.categoryList) || this.categoryList.length === 0) return
      this.$nextTick(() => {
        if (!typeId) {
          if (this.activeTab !== 0) this.activeTab = 0
          return
        }

        const idx = this.categoryList.findIndex(tab => String(tab.typeId) === String(typeId))
        if (idx !== -1) {
          if (this.activeTab !== idx) this.activeTab = idx
        } else {
          if (this.activeTab !== 0) this.activeTab = 0
        }
      })
    },
    /**
     * 切換 tab 時，更新網址 query 參數
     * @param {string|number} typeId
     */
    updateTabQuery (typeId) {
      const current = this.$route.query?.tab
      if (String(current) === String(typeId)) return
      const query = { tab: typeId }
      this.$router.replace({ query, params: { noScroll: true, toTop: false } }) // 需清除滾動參數，因為進來時可能帶有滾動參數
    },
    /**
     * 當使用者手動點擊 tab 時觸發
     * @param {object} tab - 被點擊的 tab 物件
     */
    onTabClick (tab) {
      if (tab) {
        this.updateTabQuery(tab.typeId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$contentContainerMaxWidth: calc(263px * 4 + 24px * 3);
$contentContainerMaxWidthMobile: calc(263px * 2 + 24px * 1);
@mixin layoutDefined () {
  max-width: calc(#{$contentContainerMaxWidth} + 10vw);
  margin: 0 auto;
  padding: 0 5vw;
  @media screen and (max-width: $mobileWidth) {
    max-width: calc(#{$contentContainerMaxWidthMobile} + 10vw);
  }
}
.page-service-category {
  @include layoutDefined;
  &.empty {
    display: flex;
    justify-content: center;
    align-content: center;
  }
}
.service-list-section {
  &__title {
    margin-bottom: 20px;
  }
  &__list {
    margin-top: 32px;
  }
  &__tab__loader {
    display: flex;
    .loader {
      +.loader {
        margin-left: 6px;
      }
    }
  }
}
.skeleton-card {
  &__container {
    display: flex;
    flex-wrap: wrap;
    margin-top: 40px;
    &__card {
      $cardMargin: 24px;
      width: calc((100% - #{$cardMargin} * 3) / 4);
      margin-bottom: $cardMargin;
      &:not(:nth-child(4n)) {
        margin-right: $cardMargin;
      }
    }
  }
}
.service-list-pagination {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin: 60px 0;
}
::v-deep {
  .fixed-tabs-bar {
    display: initial;
    .v-tabs-bar {
      position: sticky;
      top: $headerHeightMobile;
      z-index: 10;
      &.is-fixed {
        box-shadow: 0px 6px 6px -6px rgba(0, 0, 0, .3)
      }
    }
    .v-slide-group__prev,
    .v-slide-group__next {
      display: none !important;
    }
    @media screen and (min-width: $mobileWidth) {
      .v-tabs-bar {
        top: $headerHeight;
      }
    }
  }
}

@media screen and (max-width: $mobileWidth) {
  .page-service-category {
    &.empty {
      height: calc(100vh - #{$headerHeightMobile});
      padding-bottom: 40px;
    }
  }
  .breadcrumbs {
    margin: 0 auto;
  }
  .skeleton-card__container__card {
    $cardMargin: 16px;
    width: calc((100% - #{$cardMargin}) / 2);
    margin-bottom: $cardMargin;
    &:not(:nth-child(4n)) {
      margin-right: 0;
    }
    &:not(:nth-child(2n)) {
      margin-right: $cardMargin;
    }
  }
}
</style>
